import { ActionReducerMapBuilder, AsyncThunk, AsyncThunkConfig, Draft, SerializedError } from '@reduxjs/toolkit';

export interface AsyncState<T = any> {
  loading: boolean;
  data: T | null;
  /** The thunk's `rejectWithValue` string, else the message of what it threw. */
  error: string | null;
}

export const initAsyncState = <T = any>(data: T | null = null): AsyncState<T> => ({
  loading: false,
  data,
  error: null,
});

/** Keeps the last data through a reload or a failure, so the view does not blank out. */
export const preserveAsyncData = <T>(previous: AsyncState<T> | undefined, update: AsyncState<T>): AsyncState<T> => ({
  ...update,
  data: update.data ?? previous?.data ?? null,
});

type ThunkAction<T, Args, Config extends AsyncThunkConfig> =
  | ReturnType<AsyncThunk<T, Args, Config>['pending']>
  | ReturnType<AsyncThunk<T, Args, Config>['fulfilled']>
  | ReturnType<AsyncThunk<T, Args, Config>['rejected']>;

const errorMessage = (payload: unknown, error: unknown) =>
  typeof payload === 'string' ? payload : ((error as SerializedError | undefined)?.message ?? 'Unknown error');

/** Hands a thunk's pending, fulfilled and rejected actions to `callback` as one AsyncState. Goes after any `addCase`. */
export function addAsyncState<State, T, Args, Config extends AsyncThunkConfig = {}>(
  builder: ActionReducerMapBuilder<State>,
  thunk: AsyncThunk<T, Args, Config>,
  callback: (state: Draft<State>, asyncState: AsyncState<T>, action: ThunkAction<T, Args, Config>) => void
) {
  return builder.addAsyncThunk(thunk, {
    pending: (state, action) => callback(state, { loading: true, data: null, error: null }, action),
    fulfilled: (state, action) => callback(state, { loading: false, data: action.payload, error: null }, action),
    rejected: (state, action) => {
      // A generic thunk config hides `error` from the type, though the action always carries one.
      const error = errorMessage(action.payload, 'error' in action ? action.error : undefined);
      callback(state, { loading: false, data: null, error }, action);
    },
  });
}
